import { useContext, useState } from 'react';
import { CustomRolePermissionDto } from '../../../api-client';
import { UseMutateFunction, useQueryClient } from 'react-query';
import { NotificationsContext } from '../../../common/notifications/Notifications';
import { UpdateCustomRoleMutationArgs, useUpdateCustomRole } from './useUpdateCustomRole';
import { AxiosResponse } from 'axios';
import { ErrorResponse } from '../../../common/types/errorRespose';

type UseAddAttributeModalFacade = (id: string) => {
  isAddAttributeModalOpen: boolean;
  openAddAttributeModal: (role: CustomRolePermissionDto) => () => void;
  closeAddAttributeModal: () => void;
  addAttribute: UseMutateFunction<AxiosResponse<void>, ErrorResponse, UpdateCustomRoleMutationArgs>;
  isAddingAttribute: boolean;
  selectedPermission: CustomRolePermissionDto | null;
};

export const useAddAttributeModalFacade: UseAddAttributeModalFacade = (id) => {
  const [isAddAttributeModalOpen, setIsAddAttributeModalOpen] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState<CustomRolePermissionDto | null>(
    null,
  );

  const queryClient = useQueryClient();

  const { showNotification } = useContext(NotificationsContext);

  const openAddAttributeModal = (permission: CustomRolePermissionDto) => () => {
    setSelectedPermission(permission);
    setIsAddAttributeModalOpen(true);
  };

  const closeAddAttributeModal = () => setIsAddAttributeModalOpen(false);

  const onSuccess = () => {
    queryClient.invalidateQueries([`custom-role-${id}`]).then();
    queryClient.invalidateQueries(`custom-roles`);
    showNotification('Attribute successfully added');
    closeAddAttributeModal();
  };

  const onError = () => showNotification('An error occurred while adding an attribute');

  const { mutate: addAttribute, isLoading: isAddingAttribute } = useUpdateCustomRole({
    onError,
    onSuccess,
  });

  return {
    isAddAttributeModalOpen: isAddAttributeModalOpen && !!selectedPermission,
    openAddAttributeModal,
    closeAddAttributeModal,
    addAttribute,
    isAddingAttribute,
    selectedPermission,
  };
};
